import http from './http';

export interface VoiceConfig {
  v: number;
  voice_live: boolean;
  /** Spoken-thoughts mode active (VOICE_LIVE_ENABLED + VOICE_SPOKEN_THOUGHTS_ENABLED). */
  spoken_thoughts?: boolean;
  transport: string;
  endpointing_ms: number;
  sample_rate: number | null;
  format: string;
  channels: number;
  max_seconds: number;
  silence_seconds: number;
  max_per_user: number;
  llm: { max_tokens: number; temperature: number };
  tts: { voice: string | null; warm: boolean | null; exists: boolean | null };
}

/** Parallel voice modes. "standard" is the existing cascade loop (default). */
export type VoiceSessionMode = 'standard' | 'spoken_thoughts';


export interface VoiceSessionInfo {
  session_id: string;
  /** The mode this session runs in (absent = "standard" for older backends). */
  mode?: VoiceSessionMode | string;
  state: string;
  ws_path: string;
  sample_rate: number | null;
  format: string;
  channels: number;
  endpointing_ms: number;
  max_seconds: number;
  silence_seconds: number;
}

export interface VoiceSessionSummary {
  session_id: string;
  /** Present on GET /voice/sessions (used to match a session to a workspace). */
  user_id?: string;
  workspace_id?: string;
  mode?: VoiceSessionMode | string;
  state: string;
  reason: string | null;
  ws_attached?: boolean;
  duration_ms: number | null;
  turns: number;
  interruptions: number;
  first_audio_latency_ms: { last: number | null; avg: number | null; min: number | null; max: number | null };
}

/** GET /voice/sessions — the live sessions this user owns. */
export interface VoiceSessionsResponse {
  live: VoiceSessionSummary[];
  stats?: Record<string, unknown>;
}

export const voiceApi = {
  config: () => http.get<VoiceConfig>('/voice/config').then((r) => r.data),
  createSession: (workspaceId: string, mode?: VoiceSessionMode | string, conversationId?: string, surface?: string) =>
    http
      .post<VoiceSessionInfo>('/voice/session', {
        workspace_id: workspaceId,
        ...(mode ? { mode } : {}),
        // OpenClaw (or any specific chat): route live turns through its bridge.
        ...(conversationId ? { conversation_id: conversationId } : {}),
        // Which surface opened the session: only the floating widget ("widget")
        // may drive on-screen navigation; the chat page must not (leaving the
        // chat ends the conversation). Default on the server is "chat".
        ...(surface ? { surface } : {}),
      })
      .then((r) => r.data),
  getSession: (sessionId: string) =>
    http.get<VoiceSessionSummary>(`/voice/session/${sessionId}`).then((r) => r.data),
  /** Live sessions this user owns — used to re-attach when the page remounts. */
  sessions: () => http.get<VoiceSessionsResponse>('/voice/sessions').then((r) => r.data),
  endSession: (sessionId: string, reason = 'user') =>
    http
      .post<VoiceSessionSummary>(`/voice/session/${sessionId}/end`, { reason })
      .then((r) => r.data),
  /** Recovery path for a 429: close every live session this user owns. */
  endAllSessions: () =>
    http
      .post<{ ended: VoiceSessionSummary[]; count: number }>('/voice/sessions/end-all')
      .then((r) => r.data),
};

export default voiceApi;
